import { SearchInput } from "@marketplace/shared/ui";
import { Badge, ConditionalRendering, Text } from "@marketplace/ui";
import { getRouteApi } from "@tanstack/react-router";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ClaimStatus } from "../../dtos/get-claim-history-list-request-dto";

const route = getRouteApi("/disbursement/claim-history/");

type StatusTab = ClaimStatus | "all";

type StatusTabConfig = {
  value: StatusTab;
  dotColor?: string;
};

type StatusCounts = Partial<Record<StatusTab, number>>;

const STATUS_TABS: StatusTabConfig[] = [
  { value: "all" },
  { value: "review", dotColor: "bg-[#929618]" },
  { value: "reject", dotColor: "bg-[#FF3B30]" },
  { value: "cancelled", dotColor: "bg-fjord-grey-400" },
  { value: "approve", dotColor: "bg-[#0648FF]" },
  { value: "on_verification", dotColor: "bg-[#BD940A]" },
  { value: "payment_open", dotColor: "bg-fjord-grey-400" },
  { value: "payment_processing", dotColor: "bg-fjord-grey-400" },
  { value: "payment_success", dotColor: "bg-[#45B527]" }
];

type ClaimTabProps = {
  label: string;
  dotColor?: string;
  isActive: boolean;
  count?: number | null;
  onClick: () => void;
};

const ClaimTab: React.FC<ClaimTabProps> = ({
  label,
  dotColor,
  isActive,
  count,
  onClick
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex items-center gap-x-2 px-[10px] py-[6px] rounded-full border-solid border transition-all cursor-pointer",
        isActive
          ? "border-noble-blue-500 bg-cerulean-blue-100"
          : "border-fjord-grey-100 bg-brand-white hover:bg-fjord-grey-50"
      )}
    >
      <ConditionalRendering condition={!!dotColor}>
        <span className={clsx("w-1 h-1 rounded-full shrink-0", dotColor)} />
      </ConditionalRendering>
      <Text
        type="heading"
        size="ss"
        weight="semibold"
        className={clsx(
          "flex items-center gap-x-1.5",
          isActive ? "text-noble-blue-500" : "text-fjord-grey-400"
        )}
      >
        {label}
        <ConditionalRendering condition={!!count && count > 0}>
          <Badge className="pointer-events-none shrink-0 min-w-5 h-5 px-1.5 flex items-center justify-center rounded-full bg-mischka-100 text-[10px] text-fjord-grey-400 border-0 normal-case">
            {count}
          </Badge>
        </ConditionalRendering>
      </Text>
    </button>
  );
};

export const ClaimHistoryFilter = ({ counts }: { counts?: StatusCounts }) => {
  const { q, status } = route.useSearch();
  const navigate = route.useNavigate();
  const [search, setSearch] = useState(q ?? "");
  const { t } = useTranslation("order", { keyPrefix: "claimHistoryPage" });

  useEffect(() => {
    const timeout = setTimeout(() => {
      navigate({
        search: (prev) => ({
          ...prev,
          q: search,
          page: 1
        })
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [search, navigate]);

  const handleStatusChange = (value: StatusTab) => {
    navigate({
      search: (prev) => ({
        ...prev,
        status: value,
        page: 1
      })
    });
  };

  return (
    <div className="mb-6 space-y-4">
      <SearchInput
        className="lg:w-[634px]"
        placeholder={t("searchPlaceholder")}
        value={search}
        onChange={setSearch}
      />

      <div className="flex items-center gap-x-2 flex-wrap gap-y-2">
        <Text
          type="heading"
          size="xxs"
          weight="bold"
          className="text-fjord-grey-500"
        >
          {t("status")}
        </Text>
        {STATUS_TABS.map((tab) => (
          <ClaimTab
            key={tab.value}
            label={t(`statusTabs.${tab.value}`)}
            dotColor={tab.dotColor}
            isActive={status === tab.value}
            count={counts?.[tab.value]}
            onClick={() => handleStatusChange(tab.value)}
          />
        ))}
      </div>
    </div>
  );
};
